import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Loader2, Sparkles, Tag as TagIcon } from 'lucide-react';
import { useAIAssistant } from '@/contexts/ai-assistant/AIAssistantContext';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';

interface TagGeneratorProps {
  content: string;
  title: string;
  existingTags: string[];
  onAddTags: (tags: string[]) => void;
  onClose: () => void;
}

interface TagSuggestion {
  tag: string;
  relevance: number;
  level?: 'high' | 'specific';
}

const TagGenerator: React.FC<TagGeneratorProps> = ({
  content,
  title,
  existingTags,
  onAddTags,
  onClose
}) => {
  const { generateTags, isProcessing } = useAIAssistant();
  const [suggestions, setSuggestions] = useState<TagSuggestion[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleGenerateTags = async () => {
    setError(null);
    
    if (!content || content.trim().length === 0) {
      setError("Cannot generate tags for empty content. Please add some text to your article first.");
      return;
    }
    
    const contentSample = content.length > 3000 
      ? content.substring(0, 3000) + "..." 
      : content;
      
    const response = await generateTags(contentSample, title);
    
    if (response.success && response.data) {
      try {
        let processedData: TagSuggestion[] = [];
        
        if (Array.isArray(response.data)) {
          processedData = response.data;
        } else if (typeof response.data === 'object') {
          if (response.data.tags && Array.isArray(response.data.tags)) {
            processedData = response.data.tags;
          }
        }
        
        processedData = processedData.map(tag => ({
          ...tag,
          level: tag.level || (tag.relevance >= 8 ? 'high' : 'specific')
        }));
        
        processedData.sort((a, b) => {
          if ((a.level === 'high' && b.level === 'high') || (a.level === 'specific' && b.level === 'specific')) {
            return b.relevance - a.relevance;
          }
          return a.level === 'high' ? -1 : 1;
        });
        
        const newTags = processedData.filter(tag => 
          !existingTags.includes(tag.tag)
        );
        
        if (newTags.length > 0) {
          setSuggestions(newTags);
          const highLevelTags = newTags
            .filter(t => t.level === 'high')
            .map(t => t.tag);
            
          const topSpecificTags = newTags
            .filter(t => t.level === 'specific')
            .sort((a, b) => b.relevance - a.relevance)
            .slice(0, Math.max(0, 3 - highLevelTags.length))
            .map(t => t.tag);
            
          setSelectedTags([...highLevelTags, ...topSpecificTags]);
        } else {
          setError("No new tags found. All suggestions already exist in your article.");
        }
      } catch (err) {
        console.error("Error processing tag suggestions:", err, "Response data:", response.data);
        setError("Error processing AI response. Please try again.");
      }
    } else {
      setError(response.error || "Failed to generate tags. Please try again.");
    }
  };

  const toggleTagSelection = (tag: string) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag) 
        : [...prev, tag]
    );
  };

  const handleApplyTags = () => {
    if (selectedTags.length > 0) {
      onAddTags(selectedTags);
      onClose();
    } else {
      toast.error("Please select at least one tag to add");
    }
  };

  const getRelevanceLabel = (relevance: number) => {
    if (relevance >= 8) return "High";
    if (relevance >= 5) return "Medium";
    return "Low";
  };

  const getRelevanceColor = (relevance: number) => {
    if (relevance >= 8) return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
    if (relevance >= 5) return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
    return "bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300";
  };

  const getLevelColor = (level?: 'high' | 'specific') => {
    return level === 'high' 
      ? "bg-primary text-primary-foreground hover:bg-primary/90" 
      : "bg-secondary text-secondary-foreground hover:bg-secondary/90";
  };

  const highLevelSuggestions = suggestions.filter(s => s.level === 'high');
  const specificSuggestions = suggestions.filter(s => s.level !== 'high');

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center">
          <TagIcon className="w-5 h-5 mr-2 text-primary" />
          AI Tag Generator
        </CardTitle>
        <CardDescription>
          Generate relevant tags based on your article content
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        
        {!suggestions.length && !isProcessing && !error ? (
          <div className="flex flex-col items-center justify-center p-8">
            <p className="text-muted-foreground mb-4 text-center">
              Our AI will analyze your article and suggest relevant tags to help readers discover your content.
              We'll include both high-level categories and specific topics for better organization.
            </p>
            <Button onClick={handleGenerateTags}>
              Generate Tag Suggestions
            </Button>
          </div>
        ) : isProcessing ? (
          <div className="flex flex-col items-center justify-center p-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">
              Analyzing your article and generating relevant tags...
            </p>
          </div>
        ) : (
          <>
            <div>
              <h3 className="text-sm font-medium mb-2">Existing Tags:</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {existingTags.length > 0 ? (
                  existingTags.map(tag => (
                    <Badge key={tag} variant="secondary">{tag}</Badge>
                  ))
                ) : (
                  <p className="text-sm text-muted-foreground">No existing tags</p>
                )}
              </div>
            </div>
            
            {highLevelSuggestions.length > 0 && (
              <div>
                <h3 className="text-sm font-medium mb-2">High-Level Categories:</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {highLevelSuggestions.map((suggestion, index) => (
                    <div 
                      key={index}
                      className={`p-2 rounded-md border cursor-pointer transition-colors flex items-center justify-between ${
                        selectedTags.includes(suggestion.tag) 
                          ? 'bg-primary/20 border-primary' 
                          : 'hover:bg-accent'
                      }`}
                      onClick={() => toggleTagSelection(suggestion.tag)}
                    >
                      <span className="text-sm font-medium">{suggestion.tag}</span>
                      <Badge 
                        className={getLevelColor('high')}
                      >
                        Category
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {specificSuggestions.length > 0 && (
              <div>
                <h3 className="text-sm font-medium mb-2">Specific Topics:</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {specificSuggestions.map((suggestion, index) => (
                    <div 
                      key={index}
                      className={`p-2 rounded-md border cursor-pointer transition-colors flex items-center justify-between ${
                        selectedTags.includes(suggestion.tag) 
                          ? 'bg-primary/10 border-primary' 
                          : 'hover:bg-accent'
                      }`}
                      onClick={() => toggleTagSelection(suggestion.tag)}
                    >
                      <span className="text-sm font-medium">{suggestion.tag}</span>
                      <Badge 
                        variant="outline"
                        className={`text-xs ${getRelevanceColor(suggestion.relevance)}`}
                      >
                        {getRelevanceLabel(suggestion.relevance)}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex justify-center mt-4">
              <Button 
                variant="outline" 
                size="sm" 
                className="flex items-center gap-1"
                onClick={handleGenerateTags}
                disabled={isProcessing}
              >
                <Sparkles className="h-4 w-4" /> 
                Generate More
              </Button>
            </div>
          </>
        )}
      </CardContent>
      
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleApplyTags}
          disabled={selectedTags.length === 0 || isProcessing}
        >
          Add {selectedTags.length} {selectedTags.length === 1 ? 'Tag' : 'Tags'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default TagGenerator;
